<template>
  <div class="flex justify-end">
    <!-- Not logged in -->
    <button
      v-if="!user"
      type="button"
      @click="authService.redirectToVuoLogin()"
      class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-mocha-mousse-100 hover:bg-mocha-mousse-200 transition text-sm font-medium text-mocha-mousse-700"
    >
      <Icon icon="mdi:login" class="text-base shrink-0" />
      <span class="hidden sm:inline">Login</span>
    </button>

    <!-- Logged in -->
    <div v-else ref="dropdownRef" class="relative text-sm z-50">
      <!-- Trigger -->
      <button
        class="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg bg-mocha-mousse-100 hover:bg-mocha-mousse-200 transition"
        @click="isDropdownOpen = !isDropdownOpen"
        :aria-expanded="isDropdownOpen"
      >
        <Icon
          icon="mdi:account-circle"
          class="text-slate-500 text-lg shrink-0"
        />
        <!-- name: hidden on xs -->
        <span class="hidden sm:inline font-medium truncate max-w-[14ch]">
          {{ user.name }} {{ formattedLastname }}
        </span>
        <Icon
          :icon="isDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'"
          class="text-slate-400 text-base shrink-0"
        />
      </button>

      <!-- Dropdown panel -->
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-show="isDropdownOpen"
          class="absolute right-0 mt-2 w-44 sm:w-48 bg-mocha-mousse-50 rounded-xl shadow-xl ring-1 ring-mocha-mousse-300 overflow-hidden origin-top-right"
        >
          <!-- user name shown here on mobile since button hides it -->
          <div
            class="sm:hidden px-3 py-2 text-xs text-mocha-mousse-700 font-semibold border-b border-mocha-mousse-200 truncate"
          >
            {{ user.name }} {{ formattedLastname }}
          </div>

          <!-- Admin section -->
          <div v-if="isAdmin" class="border-b border-mocha-mousse-200">
            <div
              class="px-3 py-1.5 text-xs font-semibold text-mocha-mousse-600 uppercase tracking-wider"
            >
              Admin
            </div>
            <RouterLink
              :to="{ name: 'AdminDashboard' }"
              class="flex items-center gap-2 px-3 py-2 hover:bg-mocha-mousse-100 transition"
              @click="isDropdownOpen = false"
            >
              <Icon
                icon="mdi:view-dashboard"
                class="text-purple-600 text-base shrink-0"
              />
              Dashboard
            </RouterLink>
            <RouterLink
              :to="{ name: 'AdminLogs' }"
              class="flex items-center gap-2 px-3 py-2 hover:bg-mocha-mousse-100 transition"
              @click="isDropdownOpen = false"
            >
              <Icon
                icon="mdi:file-document-multiple"
                class="text-purple-600 text-base shrink-0"
              />
              Logs
            </RouterLink>
          </div>

          <!-- User section -->
          <div>
            <div
              v-if="isAdmin"
              class="px-3 py-1.5 text-xs font-semibold text-mocha-mousse-600 uppercase tracking-wider"
            >
              User
            </div>
            <p class="px-3 py-2 text-xs text-mocha-mousse-600">
              Logout from VUO to change account
            </p>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount, watch } from "vue";
import { Icon } from "@iconify/vue";
import authService, {
  currentUser as authServiceUser,
  currentUserIsAdmin,
} from "@/services/authService";
import { useFormStore } from "@/stores/FormStore";

const formStore = useFormStore();
const formState = formStore.formState;

const localUser = ref(null); // Local reactive ref
const isDropdownOpen = ref(false);
const dropdownRef = ref(null);

// Compute user from authService for reactivity
const user = computed(() => localUser.value ?? authServiceUser.value);

// Compute admin status from authService
const isAdmin = computed(() => currentUserIsAdmin.value);

// Watch authServiceUser for changes
watch(
  authServiceUser,
  (newValue) => {
    console.log("[LoginDropdown] authServiceUser changed:", newValue);
    localUser.value = newValue;

    if (newValue) {
      formStore.setLeadUploader({
        id: newValue?.vuo_id ?? newValue?.id,
        name: newValue?.name ?? "",
        lastname: newValue?.lastname ?? "",
        email: newValue?.vuo_email ?? newValue?.email ?? "",
        institute: newValue?.institute ?? "",
      });
    }
  },
  { immediate: true },
);

onMounted(async () => {
  console.log("[LoginDropdown] Component mounted");
  // Set initial user from authService
  localUser.value = authServiceUser.value;
  console.log(
    "[LoginDropdown] Initial user from authService:",
    localUser.value,
  );

  // No need to load user from backend - we use local reactive state
  // App.vue handles initial auth check on mount
  // Storage event listener handles multi-tab sync
  // Axios interceptor handles session expiration (401/403)
  document.addEventListener("click", handleGlobalClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleGlobalClick);
});

const formattedLastname = computed(() => {
  if (!user.value?.lastname) return "";
  return (
    user.value.lastname.charAt(0).toUpperCase() +
    user.value.lastname.slice(1).toLowerCase()
  );
});

const handleGlobalClick = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    isDropdownOpen.value = false;
  }
};
</script>
